import { motion } from 'framer-motion';
import { MessageSquare, GitBranch, UserCheck, Gift, ShieldCheck } from 'lucide-react';
import { Reveal, fadeUp, stagger } from './helpers';

const HOW_STEPS = [
    {
        num: '01',
        icon: MessageSquare,
        title: 'Three guided sessions over two weeks',
        desc: "Foundation, patterns, completeness. A trained Handoff consultant runs the conversation; an AI coach sits alongside, suggesting the right follow-up questions and detecting the unwritten rules the moment they're said. Your expert never sees the AI panel — only the consultant does.",
    },
    {
        num: '02',
        icon: GitBranch,
        title: 'We reconstruct how they think',
        desc: 'Every important call becomes a short, structured note: the situation, the signals they watched, the decision, the reasoning, the option they almost picked, the rule of thumb behind it. Your expert reviews and approves every note before it enters the playbook.',
    },
    {
        num: '03',
        icon: UserCheck,
        title: 'The successor reads it — or just asks',
        desc: "The next person in the seat reads through the playbook or asks questions in plain English. Every answer is grounded in the captured notes and cited back to the exact source. If the answer isn't in there, the assistant says so — it never invents.",
    },
    {
        num: '04',
        icon: Gift,
        title: 'The person leaving gets a copy',
        desc: "We turn what they shared into five personal documents — a profile of how they think, a record of what they built, what they're well-suited for next. Theirs to keep, in their own voice.",
    },
    {
        num: '05',
        icon: ShieldCheck,
        title: 'Knowledge stops walking out the door',
        desc: 'Resignations, restructures, promotions, internal moves: the next person inherits the playbook, not a blank page. You stop paying the six-month onboarding tax on every key departure.',
    },
];

export const HowItWorks = () => (
    <section id="how-it-works" className="py-32 scroll-mt-16 border-t border-border/40">
        <div className="max-w-6xl mx-auto px-6">
            <Reveal className="max-w-2xl mb-16">
                <p className="text-[11px] uppercase tracking-[0.2em] text-primary font-semibold mb-5">How it works</p>
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.025em] leading-[1.05]">
                    Five steps.
                    <span className="text-muted-foreground/60"> About two weeks, end to end.</span>
                </h2>
            </Reveal>

            <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: '-80px' }}
                className="space-y-px rounded-2xl overflow-hidden border border-border/60 bg-card"
            >
                {HOW_STEPS.map((step) => (
                    <motion.div key={step.num} variants={fadeUp} className="grid grid-cols-12 gap-6 px-7 py-9 bg-card hover:bg-muted/30 transition-colors group">
                        <div className="col-span-12 sm:col-span-1 flex sm:block items-center gap-3">
                            <span className="text-3xl font-bold tabular-nums text-muted-foreground/40 tracking-tight group-hover:text-primary/60 transition-colors">{step.num}</span>
                        </div>
                        <div className="col-span-12 sm:col-span-3 flex items-start gap-3">
                            <div className="w-9 h-9 rounded-lg bg-primary/8 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/15 transition-colors">
                                <step.icon className="h-4 w-4 text-primary" />
                            </div>
                            <h3 className="text-lg font-semibold tracking-tight leading-snug pt-1.5">{step.title}</h3>
                        </div>
                        <div className="col-span-12 sm:col-span-8">
                            <p className="text-base text-muted-foreground leading-relaxed">{step.desc}</p>
                        </div>
                    </motion.div>
                ))}
            </motion.div>
        </div>
    </section>
);
